import React, { useState, useEffect, useRef, useContext } from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { API_URL_rating } from "constants/api";
import { ratingReducers } from "redux/ratingSlice";
import * as Yup from "yup";
import { postData, putData } from "actions";
import { AuthContext } from "context/AuthContext";

const FormRatings = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      rating_scale: location.state ? location.state.item.rating_scale : "",
      rating_area: location.state ? location.state.item.rating_area : "",
      restrict: location.state ? location.state.item.restrict : "",
      allow_modification: location.state
        ? location.state.item.allow_modification
        : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      rating_scale: Yup.string().required("Rating scale is required"),
      rating_area: Yup.string().required("Rating Area is required"),
      restrict: Yup.string().required("Restrict is required"),
      allow_modification: Yup.string().required(
        "Allow Modification is required"
      ),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_rating,
          formData,
          { dispatch, redux: ratingReducers },
          "ADD_RATING",
          () => {
            navigate("/administrator/tickets/ratings");
          }
        );
      } else {
        putData(
          `${API_URL_rating}${location.state.item.id}/`,
          formData,
          { dispatch, redux: ratingReducers },
          "ADD_RATING",
          () => {
            navigate("/administrator/tickets/ratings");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Create Ratings"}
        excludedPaths={["tickets", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          {typeForm === "create" ? "Create" : "Edit"}
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3 pb-5">
              <div className="col-span-full mb-1.5 md:col-span-full md:col-start-1 lg:col-span-full lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Rating label <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full">
                <label className="text-sm">
                  Rating scale <span className="text-red-500">*</span>
                </label>

                <div className="h-auto w-full grid items-center px-4 py-2 bg-white dark:bg-neutral-700 border border-gray-300 border-l-8 italic dark:border-neutral-600 mb-3">
                  <label className="text-sm">
                    The maximum rating that can be given. For example, if 5 is
                    selected, the lowest possible rating will be 1 and the
                    highest 5.
                  </label>
                </div>

                <div>
                  <select
                    id="rating_scale"
                    name="rating_scale"
                    value={formik.values.rating_scale}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6 mb-5"
                  >
                    <option value="">Select</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                  {formik.touched.rating_scale &&
                    formik.errors.rating_scale && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.rating_scale}
                      </div>
                    )}
                </div>

                <label className="text-sm">
                  Rating area <span className="text-red-500">*</span>
                </label>

                <div>
                  <select
                    id="rating_area"
                    name="rating_area"
                    value={formik.values.rating_area}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6 mb-5"
                  >
                    <option value="">Select</option>
                    <option value="Helpdesk Area">Helpdesk Area</option>
                  </select>
                  {formik.touched.rating_area && formik.errors.rating_area && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.rating_area}
                    </div>
                  )}
                </div>

                <label className="text-sm">
                  Restrict rating to a department
                  <span className="text-red-500">*</span>
                </label>

                <div className="h-auto w-full grid items-center px-4 py-2 bg-white dark:bg-neutral-700 border border-gray-300 border-l-8 italic dark:border-neutral-600 mb-3">
                  <label className="text-sm">
                    Select a department to restrict this rating to tickets or
                    chats within a specific department. If no department is
                    selected, the rating will appear across all departments.
                  </label>
                </div>

                <div>
                  <select
                    id="restrict"
                    name="restrict"
                    value={formik.values.restrict}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[3] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6 mb-5"
                  >
                    <option value="">Select</option>
                    <option value="general">General</option>
                  </select>
                  {formik.touched.restrict && formik.errors.restrict && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.restrict}
                    </div>
                  )}
                </div>

                <label className="text-sm">
                  Allow user to change the rating?{" "}
                  <span className="text-red-500">*</span>
                </label>

                <div className="h-auto w-full grid items-center px-4 py-2 bg-white dark:bg-neutral-700 border border-gray-300 border-l-8 italic dark:border-neutral-600 mb-3">
                  <label className="text-sm">
                    If you choose YES user can modify the rating.
                  </label>
                </div>

                <div className="flex flex-wrap">
                  <label className="inline-flex items-center text-xs md:-text-sm lg:text-sm">
                    <input
                      type="radio"
                      id="allow_modification1"
                      name="allow_modification"
                      ref={(ref) => (inputRefs.current[4] = ref)}
                      checked={formik.values.allow_modification === 1}
                      onChange={() =>
                        formik.setFieldValue("allow_modification", 1)
                      }
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2 mr-5">Yes</span>
                  </label>
                  <label className="inline-flex items-center text-xs md:-text-sm lg:text-sm">
                    <input
                      type="radio"
                      id="allow_modification0"
                      name="allow_modification"
                      ref={(ref) => (inputRefs.current[5] = ref)}
                      checked={formik.values.allow_modification === 0}
                      onChange={() =>
                        formik.setFieldValue("allow_modification", 0)
                      }
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2">No</span>
                  </label>
                  {formik.touched.allow_modification &&
                    formik.errors.allow_modification && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.allow_modification}
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormRatings;
